<template>
  <!-- Renderless: the popup itself is drawn globally by GlobalDialog (mounted as
       <LazyGlobalDialog /> in layouts/default.vue) once we push into the dialog store below.
       vue/valid-template-root (vue3-essential) requires a root ELEMENT — a comment-only
       template is reported as "noChild" — so this inert span satisfies the rule without
       rendering anything. -->
  <span v-if="false" />
</template>

<script setup lang="ts">
import { computed, watch, onMounted, onBeforeUnmount, useRoute, useRouter } from '#imports';
import { useI18n } from 'vue-i18n';
import { useSessionOwner } from '~/composables/useSessionOwner';
import { useAssessmentSeat } from '~/composables/useAssessmentSeat';
import { useGenericDialog } from '~/composables/useGenericDialog';
import { useDialogStore } from '~/store/dialog/dialogStore';
import { RouteName } from '~~/package/enum/RouteName';

// The scope comes in as a prop rather than being derived here, because app.vue must key this
// component on the same value: useSessionOwner() binds its channel and storage key once at
// setup, so a guarded→guarded navigation has to remount rather than mutate in place.
const props = defineProps<{ scope: string }>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const dialogStore = useDialogStore();
const { openGenericAlert } = useGenericDialog();

const { isOwner, isStarted, start, stop } = useSessionOwner(props.scope);
const { seatLost } = useAssessmentSeat();

// Two independent ways to lose the screen, one popup:
//   - another TAB in this browser claimed it (instant, BroadcastChannel)
//   - another DEVICE claimed the server-side seat (<=30s, session-state poll)
const isDisplaced = computed(() => (isStarted.value && !isOwner.value) || seatLost.value);

// Per-instance on purpose (NOT module-level — module scope would leak across remounts and
// across tests): guards against opening the popup twice when displacement fires more than
// once (e.g. a foreign tab claim followed later by a seat-loss poll).
let opened = false;

/**
 * Spec §4: 확인 / X → close and go to the assessment start page. Deliberately NOT a
 * "use here" reclaim — that contradicts "기존 화면은 종료" and lets two tabs trade the
 * seat back and forth forever.
 */
const leave = (): void => {
    const assessmentId = route.params.assessmentId;
    if (assessmentId) {
        // Named route matters: the taking page's onBeforeRouteLeave
        // (application/pages/learning/proSkillAssure/[assessmentId]/[taskId].vue:166) only
        // permits leaving to a `to.name` whitelist, which includes this route (and RouteName.Home).
        router.replace({ name: RouteName.LearningProSkillAssureAssessmentId, params: { assessmentId } });
        return;
    }
    router.replace('/');
};

watch(isDisplaced, (displaced) => {
    if (!displaced || opened) return;
    opened = true;
    openGenericAlert(
        {
            title: t('SkillCertify-duplicate-session-warning-title'),
            contents: t('SkillCertify-duplicate-session-warning-description'),
            // popupClass intentionally omitted: the default 'dark basic' is the 70% dim +
            // 448px card the spec calls for. Passing 'dark' would add .black to .popUpWrap
            // and make the backdrop opaque, hiding the whole screen instead of just dimming it.
            //
            // The old <dialog>.showModal() made the document inert, which took focus AWAY from
            // the editor underneath. A plain div does not, so without this the taking screen
            // keeps keyboard focus and typing still lands in CodeMirror — and on a frontend
            // multi-file task that fires a 1s-debounced save straight to S3 (no seat header,
            // so the server cannot reject it), clobbering the active screen's answer files.
            autofocusConfirm: true,
        },
        { confirm: { event: leave }, close: { event: leave } },
    );
});

onMounted(() => start());
onBeforeUnmount(() => {
    // Don't let a still-open popup linger onto the next page.
    if (opened && dialogStore.show) dialogStore.close();
    stop();
});
</script>
